import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Loader2, Sparkles, CheckCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "../../Authentication/components/RealTimeAuthStatus";
import { useSupabase } from "../supabase/supabase";
import { useAnalytics } from "../hooks/useAnalytics";
import { retrySupabaseOnFail } from "../utils/retry-supabase";
import "../../Authentication/assets/GoogleButton.css";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { signInWithGoogle, signInWithApple, isLoading, session } = useAuth();
  const supabase = useSupabase();
  const analytics = useAnalytics();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSigningInWithApple, setIsSigningInWithApple] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  // Listen for window focus to detect OAuth return
  useEffect(() => {
    const handleFocus = () => {
      if (isSigningIn) {
        setTimeout(() => {
          if (isSigningIn && !session) {
            setIsSigningIn(false);
          }
        }, 1000);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isSigningIn, session]);

  // Listen for successful auth from OAuth flow
  useEffect(() => {
    const handleAuthSuccess = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.session) {
        // Determine which provider was used based on state
        const provider = isSigningInWithApple ? "apple" : "google";
        setIsSigningIn(false);
        setIsSigningInWithApple(false);
        setError(null);
        analytics.webappLoginSuccessful(provider);
      }
    };

    window.addEventListener("supabase-auth-success", handleAuthSuccess);
    return () =>
      window.removeEventListener("supabase-auth-success", handleAuthSuccess);
  }, [analytics, isSigningInWithApple]);

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      setError(null);
      analytics.webappLoginSelected("google");
      await signInWithGoogle();
    } catch (error) {
      console.error("Sign in error:", error);
      setError(error instanceof Error ? error.message : "Sign in failed");
      setIsSigningIn(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setIsSigningInWithApple(true);
      setError(null);
      analytics.webappLoginSelected("apple");
      await signInWithApple();
    } catch (error) {
      console.error("Apple sign in error:", error);
      setError(error instanceof Error ? error.message : "Apple sign in failed");
      setIsSigningInWithApple(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsEmailLoading(true);

    analytics.webappLoginSelected("email");

    try {
      if (isSignUp) {
        if (password.length < 6) {
          throw new Error("Password must be at least 6 characters long");
        }

        if (!supabase) {
          throw new Error("Supabase client not initialized");
        }
        const { data, error } = await retrySupabaseOnFail(() =>
          supabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: undefined,
            },
          })
        );

        if (error) throw error;

        if (data.user) {
          const { error: signInError } = await retrySupabaseOnFail(() =>
            supabase?.auth.signInWithPassword({
              email,
              password,
            })
          );

          if (signInError) throw signInError;
          analytics.webappLoginSuccessful("email");
        }
      } else {
        if (!supabase) {
          throw new Error("Supabase client not initialized");
        }
        const { error } = await retrySupabaseOnFail(() =>
          supabase.auth.signInWithPassword({
            email,
            password,
          })
        );

        if (error) throw error;
        analytics.webappLoginSuccessful("email");
      }
    } catch (err) {
      console.error("Email auth error:", err);
      let errorMessage = "Authentication failed";
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsEmailLoading(true);

    try {
      analytics.webappLoginSelected("magic_link");

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        throw error;
      }

      setMagicLinkSent(true);
    } catch (err) {
      console.error("Magic link error:", err);
      setError(
        err instanceof Error ? err.message : "Failed to send magic link"
      );
    } finally {
      setIsEmailLoading(false);
    }
  };

  const resetForm = () => {
    setShowEmailForm(false);
    setShowMagicLink(false);
    setMagicLinkSent(false);
    setEmail("");
    setPassword("");
    setError(null);
    setIsSignUp(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Success state
  if (session) {
    return ReactDOM.createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999]"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="fixed inset-0 flex items-center justify-center z-[10000] px-4"
        >
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="px-8 py-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
                className="w-20 h-20 bg-gradient-to-br from-green-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle
                  className="w-10 h-10 text-green-600"
                  strokeWidth={1.5}
                />
              </motion.div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Welcome back!
              </h2>
              <p className="text-gray-600 text-sm">
                Redirecting you to the app...
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999]"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className="fixed inset-0 flex items-center justify-center z-[10000] px-4"
            onClick={handleClose}
          >
            <div
              className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-8 pt-8 pb-6">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleClose}
                  className="absolute top-6 right-6 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                </motion.button>

                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-center"
                >
                  {/* Icon with animation */}
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-sky-50 to-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    animate={{
                      rotate: [0, -5, 5, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  >
                    <Sparkles
                      className="w-8 h-8 text-sky-600"
                      strokeWidth={1.5}
                    />
                  </motion.div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    {showMagicLink
                      ? magicLinkSent
                        ? "Check your email"
                        : "Sign in with magic link"
                      : showEmailForm
                      ? isSignUp
                        ? "Create your account"
                        : "Welcome back"
                      : "Welcome to WriteVoice"}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {showMagicLink
                      ? magicLinkSent
                        ? `We've sent a magic link to ${email}`
                        : "Enter your email and we'll send you a link to sign in"
                      : showEmailForm
                      ? isSignUp
                        ? "Get started with WriteVoice"
                        : "Sign in to continue"
                      : "Sign in to save your settings and track productivity"}
                  </p>
                </motion.div>
              </div>

              {/* Body */}
              <div className="px-8 pb-8">
                {showMagicLink ? (
                  magicLinkSent ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      <div className="bg-sky-50 rounded-2xl p-4">
                        <p className="text-sm text-gray-700 text-center">
                          Click the link in the email to sign in. The link will
                          redirect you back to this app.
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setShowMagicLink(false);
                          setMagicLinkSent(false);
                          setEmail("");
                        }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Back to sign in
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <button
                        onClick={() => {
                          setShowMagicLink(false);
                          setError(null);
                        }}
                        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </button>

                      <form onSubmit={handleMagicLink} className="space-y-4">
                        <div>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all"
                            required
                            autoFocus
                          />
                        </div>

                        <motion.button
                          type="submit"
                          disabled={isEmailLoading || !email}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full relative overflow-hidden group"
                        >
                          <div className="relative px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl flex items-center justify-center shadow-lg">
                            {isEmailLoading ? (
                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                            ) : (
                              <span className="text-white font-medium">
                                Send Magic Link
                              </span>
                            )}
                          </div>
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                            initial={{ x: "-100%" }}
                            whileHover={{ x: "100%" }}
                            transition={{ duration: 0.5 }}
                          />
                        </motion.button>
                      </form>

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-4 text-sm text-red-600 text-center bg-red-50 rounded-2xl px-4 py-3"
                        >
                          {error}
                        </motion.div>
                      )}
                    </motion.div>
                  )
                ) : showEmailForm ? (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                      <div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all"
                          required
                          autoFocus
                        />
                      </div>

                      <div>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Password"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all"
                          required
                          minLength={6}
                        />
                        {isSignUp &&
                          password.length > 0 &&
                          password.length < 6 && (
                            <p className="text-xs text-red-500 mt-1 px-4">
                              Password must be at least 6 characters
                            </p>
                          )}
                      </div>

                      <motion.button
                        type="submit"
                        disabled={
                          isEmailLoading || (isSignUp && password.length < 6)
                        }
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full relative overflow-hidden group"
                      >
                        <div className="relative px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl flex items-center justify-center shadow-lg">
                          {isEmailLoading ? (
                            <Loader2 className="w-5 h-5 text-white animate-spin" />
                          ) : (
                            <span className="text-white font-medium">
                              {isSignUp ? "Create Account" : "Sign In"}
                            </span>
                          )}
                        </div>
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "100%" }}
                          transition={{ duration: 0.5 }}
                        />
                      </motion.button>
                    </form>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 text-sm text-red-600 text-center bg-red-50 rounded-2xl px-4 py-3"
                      >
                        {error}
                      </motion.div>
                    )}

                    <div className="mt-6 space-y-3">
                      <button
                        onClick={() => setIsSignUp(!isSignUp)}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {isSignUp
                          ? "Already have an account? Sign in"
                          : "Don't have an account? Sign up"}
                      </button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="px-4 bg-white text-gray-400">
                            or
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setShowEmailForm(false);
                          setError(null);
                        }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Back to other options
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-3"
                  >
                    <motion.button
                      onClick={handleGoogleSignIn}
                      disabled={isLoading || isSigningIn || isSigningInWithApple}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    >
                      {isSigningIn ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-3"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="#EA4335"
                              d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.43C17.95 1.19 15.23 0 12 0 7.31 0 3.26 2.69 1.28 6.61l3.99 3.09C6.21 6.86 8.87 4.75 12 4.75z"
                            />
                            <path
                              fill="#4285F4"
                              d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.86 3c2.26-2.09 3.56-5.17 3.56-8.82z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.26 14.29c-.25-.73-.38-1.49-.38-2.29s.14-1.57.38-2.29L1.28 6.61A11.97 11.97 0 000 12c0 1.94.46 3.77 1.28 5.39l3.98-3.1z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96l-3.99 3.09C3.26 21.31 7.31 24 12 24z"
                            />
                          </svg>
                          Continue with Google
                        </>
                      )}
                    </motion.button>

                    {/* Apple Sign In Button */}
                    <motion.button
                      onClick={handleAppleSignIn}
                      disabled={isLoading || isSigningIn || isSigningInWithApple}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    >
                      {isSigningInWithApple ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 mr-3"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                          </svg>
                          Continue with Apple
                        </>
                      )}
                    </motion.button>

                    <motion.button
                      onClick={() => setShowEmailForm(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    >
                      <Mail className="w-5 h-5 mr-3" />
                      Continue with Email
                    </motion.button>

                    <motion.button
                      onClick={() => setShowMagicLink(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    >
                      <Sparkles className="w-5 h-5 mr-3" />
                      Continue with Magic Link
                    </motion.button>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-600 text-center bg-red-50 rounded-2xl px-4 py-3"
                      >
                        {error}
                      </motion.div>
                    )}

                    <p className="text-xs text-gray-400 text-center pt-4">
                      By using WriteVoice, you agree to our{" "}
                      <a
                        href="https://www.writevoice.io/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-500 hover:text-sky-600 underline"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://www.writevoice.io/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-500 hover:text-sky-600 underline"
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Bottom decorative element */}
              <motion.div
                className="h-1 bg-gradient-to-r from-sky-400 via-sky-500 to-sky-400"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  backgroundSize: "200% 100%",
                }}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LoginModal;
